"use client"

import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import toast from "react-hot-toast"
import {
  FaPlus,
  FaUpload,
  FaFileExcel,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaEdit,
  FaBan,
  FaPause,
  FaPlay,
  FaSearch,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaClock,
  FaHourglass,
  FaTimesCircle,
} from "react-icons/fa"
import {
  FiFilter,
  FiX,
  FiMapPin,
  FiCalendar,
  FiSettings,
  FiSearch,
  FiRefreshCw,
  FiUser,
} from "react-icons/fi"
import { LuEye } from "react-icons/lu"
import api from "../../libs/axios"
import { useAuth } from "../../provider/auth.provider"
import { useDebouncedCallback } from "../../hooks/useDebounce"
import { Pagination, PaginationType } from "../../utils/pagination.utils"
import { ExcelDownload } from "../../utils/excel.utils"
import { dateRangeInputBounds, firstDateRangeError } from "../../utils/date-range.utils"
import MaterialModeBadge, { MATERIAL_MODE_OPTIONS, materialModeLabel } from "./components/MaterialModeBadge"
import {
  SearchableSelect,
  SelectWithSearch,
  type SelectOption,
} from "../../utils/select-with-search"

export const INC_WORKFLOW_OPTIONS = [
  { value: "site_created", label: "Site Created" },
  { value: "team_assigned", label: "Team Assigned" },
  { value: "material_planned", label: "Material Planned" },
  { value: "material_requested", label: "Material Requested" },
  { value: "material_issued", label: "Material Issued" },
  { value: "material_delivered", label: "Material Delivered" },
  { value: "work_started", label: "Site Accepted" },
  { value: "work_in_progress", label: "Work In Progress" },
  { value: "visit_completed", label: "Work Completed" },
  { value: "measurement_book_pending", label: "MB Pending" },
  { value: "measurement_book_approved", label: "MB Approved" },
  { value: "boq_pending", label: "BOQ Pending" },
  { value: "boq_finalized", label: "BOQ Finalized" },
  { value: "billing_pending", label: "Billing Pending" },
  { value: "closed", label: "Billing Done" },
  { value: "cancelled", label: "Cancelled" },
]

export const INC_STATUS_OPTIONS = [
  { value: "in_progress", label: "In Progress" },
  { value: "hold", label: "Hold" },
  { value: "cancelled", label: "Cancelled" },
  { value: "completed", label: "Completed" },
  { value: "closed", label: "Closed" },
]

export const INC_PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
]

export const INC_WORK_TYPE_OPTIONS = [
  { value: "INC", label: "INC" },
  { value: "OME", label: "OME" },
]

export const INC_TEAM_TYPE_OPTIONS = [
  { value: "inhouse", label: "In-house" },
  { value: "vendor", label: "Vendor" },
]

// ── Badges ──────────────────────────────────────────────────────────────────
type BadgeConfig = { color: string; icon: React.ComponentType<{ className?: string }>; text: string }

const statusConfig: Record<string, BadgeConfig> = {
  in_progress: { color: "bg-yellow-100 text-yellow-800", icon: FaHourglass, text: "In Progress" },
  hold: { color: "bg-stone-200 text-stone-900", icon: FaPause, text: "Hold" },
  cancelled: { color: "bg-red-200 text-red-900", icon: FaTimesCircle, text: "Cancelled" },
  completed: { color: "bg-green-100 text-green-800", icon: FaCheckCircle, text: "Completed" },
  closed: { color: "bg-emerald-100 text-emerald-800", icon: FaCheckCircle, text: "Closed" },
}

const getStatusBadge = (status: string) => {
  const config = statusConfig[status] || {
    color: "bg-gray-100 text-gray-800 capitalize",
    icon: FaMapMarkerAlt,
    text: (status || "-").replace(/_/g, " "),
  }
  const Icon = config.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className="w-2 h-2" />
      {config.text}
    </span>
  )
}

const workflowColor = (wf: string): string => {
  if (wf === "cancelled") return "bg-red-100 text-red-800"
  if (wf === "closed") return "bg-emerald-100 text-emerald-800"
  if (wf === "site_created") return "bg-sky-100 text-sky-800"
  if (wf.startsWith("material")) return "bg-teal-100 text-teal-800"
  if (wf.startsWith("work") || wf === "visit_completed") return "bg-amber-100 text-amber-800"
  if (wf.startsWith("measurement")) return "bg-violet-100 text-violet-800"
  if (wf.startsWith("boq")) return "bg-cyan-100 text-cyan-800"
  if (wf.startsWith("billing")) return "bg-blue-100 text-blue-800"
  return "bg-gray-100 text-gray-800"
}

const getWorkflowBadge = (wf: string) => {
  const label = INC_WORKFLOW_OPTIONS.find((o) => o.value === wf)?.label || (wf || "-").replace(/_/g, " ")
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${workflowColor(wf)}`}>
      <FaClock className="w-2 h-2" />
      {label}
    </span>
  )
}

const priorityConfig: Record<string, string> = {
  critical: "bg-red-100 text-red-800",
  high: "bg-orange-100 text-orange-800",
  medium: "bg-blue-100 text-blue-800",
  low: "bg-gray-100 text-gray-700",
}

const getPriorityBadge = (p?: string) => (
  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${priorityConfig[p || ""] || "bg-gray-100 text-gray-700"}`}>
    {(p || "-").replace(/_/g, " ")}
  </span>
)

// ── Filters ─────────────────────────────────────────────────────────────────
// Laid out like the Project Sites filter (Site Info / Location / Dates /
// Assignments / Stages), with the project multi-select on every tab.
export interface IncFilters {
  // Site info
  project_id?: string // CSV of project ids
  activity_id?: string
  site_code?: string
  site_name?: string
  reference_number?: string
  po_number?: string
  work_type?: string
  // Location
  state_circle_id?: string
  district_id?: string
  pincode?: string
  // Dates
  created_from?: string
  created_to?: string
  allocation_from?: string
  allocation_to?: string
  // Assignments (inc_site_assignments)
  team_type?: string
  has_team?: string
  has_supervisor?: string
  team_member_id?: string // CSV of user ids
  supervisor_id?: string
  // Stages
  status_list?: string
  workflow_status_list?: string
  material_mode?: string
  priority_list?: string
}

const FILTER_LABELS: Record<string, string> = {
  project_id: "Project",
  activity_id: "Activity",
  site_code: "Site Code",
  site_name: "Site Name",
  reference_number: "Reference No.",
  po_number: "PO Number",
  work_type: "Work Type",
  state_circle_id: "State",
  district_id: "District",
  pincode: "Pincode",
  created_from: "Created From",
  created_to: "Created To",
  allocation_from: "Allocated From",
  allocation_to: "Allocated To",
  team_type: "Team Type",
  has_team: "Has Team Assigned",
  has_supervisor: "Has Supervisor",
  team_member_id: "Team Members",
  supervisor_id: "Supervisor",
  status_list: "Status",
  workflow_status_list: "Workflow",
  material_mode: "Material",
  priority_list: "Priority",
}

const YES_NO_LABEL: Record<string, string> = { true: "Yes", false: "No" }

const inputClass =
  "w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
const labelClass = "block text-sm font-medium text-gray-700 mb-2"

export const teamMemberName = (user: any): string =>
  [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.username || ""

const teamMemberLabel = (user: any): string => {
  const designation = user?.designation?.name
  return designation ? `${teamMemberName(user)} (${designation})` : teamMemberName(user)
}

// Every page of a paginated list endpoint (the server caps a page at 100).
const fetchAllPages = async (url: string, key: string, params: Record<string, any> = {}): Promise<any[]> => {
  const all: any[] = []
  for (let page = 1; page <= 20; page++) {
    const res = await api.get(url, { params: { ...params, page, limit: 100 } })
    const data = res.data?.data ?? {}
    all.push(...(data[key] ?? []))
    if (!data.pagination?.has_next_page) break
  }
  return all
}

type Opt = { value: number | string; label: string }

interface FilterModalProps {
  isOpen: boolean
  /** "mine" on My INC Sites: team-member options come from the caller's own sites. */
  teamScope: "all" | "mine"
  initial: IncFilters
  initialLabels: Record<string, string>
  onClose: () => void
  onApply: (filters: IncFilters, labels: Record<string, string>) => void
  onReset: () => void
}

const FILTER_TABS = [
  { id: "basic", label: "Site Info", icon: FiSearch },
  { id: "location", label: "Location", icon: FiMapPin },
  { id: "dates", label: "Dates", icon: FiCalendar },
  { id: "assignments", label: "Assignments", icon: FiUser },
  { id: "status", label: "Stages", icon: FiSettings },
]

const csvIds = (value?: string): string[] =>
  String(value || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean)

const IncFilterModal: React.FC<FilterModalProps> = ({ isOpen, teamScope, initial, initialLabels, onClose, onApply, onReset }) => {
  const [activeTab, setActiveTab] = useState("basic")
  const [draft, setDraft] = useState<IncFilters>(initial)
  const [labels, setLabels] = useState<Record<string, string>>(initialLabels)
  const mkOpt = (raw?: string, lbl?: string): SelectOption | null =>
    raw ? { id: Number(raw), value: raw, label: lbl || String(raw) } : null
  const [stateOpt, setStateOpt] = useState<SelectOption | null>(mkOpt(initial.state_circle_id, initialLabels.state_circle_id))
  const [districtOpt, setDistrictOpt] = useState<SelectOption | null>(mkOpt(initial.district_id, initialLabels.district_id))

  // Option lists, loaded once per open.
  const [projects, setProjects] = useState<any[]>([])
  const [teamMembers, setTeamMembers] = useState<any[]>([])
  const [supervisors, setSupervisors] = useState<any[]>([])
  const [optionsLoading, setOptionsLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setDraft(initial)
    setLabels(initialLabels)
    setStateOpt(mkOpt(initial.state_circle_id, initialLabels.state_circle_id))
    setDistrictOpt(mkOpt(initial.district_id, initialLabels.district_id))

    let cancelled = false
    setOptionsLoading(true)
    Promise.all([
      // Only the caller's own projects, as before.
      fetchAllPages("/projects/list", "projects", { status: "ACTIVE", assigned_only: true }),
      fetchAllPages("/inc/sites/team-members", "users", { scope: teamScope }),
      fetchAllPages("/inc/sites/team-members", "users", { scope: teamScope, supervisors_only: true }),
    ])
      .then(([p, t, s]) => {
        if (cancelled) return
        setProjects(p)
        setTeamMembers(t)
        setSupervisors(s)
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load filter options")
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, teamScope])

  const projectOptions: Opt[] = useMemo(
    () => projects.map((p) => ({ value: p.id, label: `${p.project_name} (${p.id})` })),
    [projects],
  )
  // Activities of the caller's own projects — so every option can return rows.
  const activityOptions: Opt[] = useMemo(() => {
    const byId = new Map<number, string>()
    projects.forEach((p) => {
      if (p.activity?.id) byId.set(p.activity.id, p.activity.activity_name)
    })
    return Array.from(byId, ([id, name]) => ({ value: id, label: `${name} (${id})` })).sort((a, b) =>
      a.label.localeCompare(b.label),
    )
  }, [projects])
  const teamMemberOptions: Opt[] = useMemo(
    () => teamMembers.map((u) => ({ value: u.id, label: teamMemberLabel(u) })),
    [teamMembers],
  )
  const supervisorOptions: Opt[] = useMemo(
    () => supervisors.map((u) => ({ value: u.id, label: teamMemberLabel(u) })),
    [supervisors],
  )

  if (!isOpen) return null

  const set = (key: keyof IncFilters, value: string, label?: string) => {
    setDraft((prev) => ({ ...prev, [key]: value || undefined }))
    if (label !== undefined) setLabels((prev) => ({ ...prev, [key]: label }))
  }

  // Multi-select bound to a CSV filter key; chip label is the picked names.
  const multiSelect = (key: keyof IncFilters, options: Opt[], placeholder: string) => {
    const selected = csvIds(draft[key])
    return (
      <SearchableSelect
        value={options.filter((o) => selected.includes(String(o.value)))}
        onChange={(opts: Opt[] = []) =>
          set(
            key,
            opts.map((o) => o.value).join(","),
            opts.map((o) => o.label).join(", "),
          )
        }
        options={options}
        placeholder={placeholder}
        isMulti
        showSelectAllVisible
        isLoading={optionsLoading}
        portalMenu
      />
    )
  }

  // Single select bound to a filter key.
  const singleSelect = (key: keyof IncFilters, options: Opt[], placeholder: string) => (
    <SearchableSelect
      value={options.find((o) => String(o.value) === String(draft[key] ?? "")) || null}
      onChange={(opt: Opt | null) => set(key, opt ? String(opt.value) : "", opt?.label || "")}
      options={options}
      placeholder={placeholder}
      isLoading={optionsLoading}
      portalMenu
    />
  )

  const yesNoSelect = (key: keyof IncFilters) => (
    <select
      className={inputClass}
      value={draft[key] || ""}
      onChange={(e) => set(key, e.target.value, YES_NO_LABEL[e.target.value] || "")}
    >
      <option value="">All</option>
      <option value="true">Yes</option>
      <option value="false">No</option>
    </select>
  )

  const textInput = (key: keyof IncFilters, placeholder: string, type = "text") => (
    <input
      type={type}
      className={inputClass}
      placeholder={placeholder}
      value={draft[key] || ""}
      onChange={(e) => set(key, e.target.value, e.target.value)}
    />
  )

  const optionSelect = (key: keyof IncFilters, options: { value: string; label: string }[]) => (
    <select
      className={inputClass}
      value={draft[key] || ""}
      onChange={(e) => set(key, e.target.value, options.find((o) => o.value === e.target.value)?.label || "")}
    >
      <option value="">All</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )

  const projectField = (
    <div>
      <label className={labelClass}>Project Name (Search)</label>
      {multiSelect("project_id", projectOptions, "Search projects")}
    </div>
  )

  const activityField = (
    <div>
      <label className={labelClass}>Activity (Search)</label>
      {singleSelect("activity_id", activityOptions, "Search activity")}
    </div>
  )

  const dateRange = (title: string, fromKey: keyof IncFilters, toKey: keyof IncFilters) => {
    const bounds = dateRangeInputBounds(draft[fromKey], draft[toKey])
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>From</label>
            <input type="date" className={inputClass} value={draft[fromKey] || ""} onChange={(e) => set(fromKey, e.target.value, e.target.value)} {...bounds.from} />
          </div>
          <div>
            <label className={labelClass}>To</label>
            <input type="date" className={inputClass} value={draft[toKey] || ""} onChange={(e) => set(toKey, e.target.value, e.target.value)} {...bounds.to} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-2xl max-w-5xl w-full max-h-[90vh] min-h-[520px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <FiFilter className="w-5 h-5" /> Filters
            </h2>
            <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {FILTER_TABS.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600 bg-white"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === "basic" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projectField}
                <div>
                  <label className={labelClass}>Site Code</label>
                  {textInput("site_code", "Enter Site Code")}
                </div>
                <div>
                  <label className={labelClass}>Site Name</label>
                  {textInput("site_name", "Enter Site Name")}
                </div>
                {activityField}
                <div>
                  <label className={labelClass}>Reference Number</label>
                  {textInput("reference_number", "Enter Reference Number")}
                </div>
                <div>
                  <label className={labelClass}>Work Type</label>
                  {optionSelect("work_type", INC_WORK_TYPE_OPTIONS)}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">PO Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className={labelClass}>PO Number</label>
                    {textInput("po_number", "PO Number")}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "location" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projectField}
              <div>
                <label className={labelClass}>State Circles</label>
                <SelectWithSearch
                  placeholder="Select State Circle"
                  apiEndpoint="/state-circle/list"
                  dataKey="states"
                  labelKey="state_name"
                  value={stateOpt}
                  onChange={(opt) => {
                    setStateOpt(opt)
                    setDistrictOpt(null)
                    setDraft((prev) => ({ ...prev, state_circle_id: opt ? String(opt.value) : undefined, district_id: undefined }))
                    setLabels((p) => ({ ...p, state_circle_id: opt?.label || "", district_id: "" }))
                  }}
                />
              </div>
              <div>
                <label className={labelClass}>Districts</label>
                <SelectWithSearch
                  placeholder="Select District"
                  apiEndpoint="/state-circle"
                  dataKey="districts"
                  labelKey="name"
                  dependencyId={stateOpt ? Number(stateOpt.value) : null}
                  disabled={!stateOpt}
                  value={districtOpt}
                  onChange={(opt) => {
                    setDistrictOpt(opt)
                    set("district_id", opt ? String(opt.value) : "", opt?.label || "")
                  }}
                />
              </div>
              <div>
                <label className={labelClass}>Pincode</label>
                {textInput("pincode", "Pincode", "number")}
              </div>
            </div>
          )}

          {activeTab === "dates" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className={labelClass}>Project Name (Search)</label>
                <div className="max-w-sm">{multiSelect("project_id", projectOptions, "Search projects")}</div>
              </div>
              {dateRange("Site Created Date", "created_from", "created_to")}
              {dateRange("Client Allocation Date", "allocation_from", "allocation_to")}
            </div>
          )}

          {activeTab === "assignments" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projectField}
              <div>
                <label className={labelClass}>Has Team Assigned</label>
                {yesNoSelect("has_team")}
              </div>
              <div>
                <label className={labelClass}>Has Supervisor</label>
                {yesNoSelect("has_supervisor")}
              </div>
              <div>
                <label className={labelClass}>Team Type</label>
                {optionSelect("team_type", INC_TEAM_TYPE_OPTIONS)}
              </div>
              <div>
                <label className={labelClass}>Team Members</label>
                {multiSelect("team_member_id", teamMemberOptions, "Search team members")}
              </div>
              <div>
                <label className={labelClass}>Supervisor</label>
                {singleSelect("supervisor_id", supervisorOptions, "Search supervisor")}
              </div>
            </div>
          )}

          {activeTab === "status" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projectField}
              {activityField}
              <div>
                <label className={labelClass}>Work Status</label>
                {optionSelect("status_list", INC_STATUS_OPTIONS)}
              </div>
              <div>
                <label className={labelClass}>Workflow Stage</label>
                {optionSelect("workflow_status_list", INC_WORKFLOW_OPTIONS)}
              </div>
              <div>
                <label className={labelClass}>Material</label>
                {optionSelect("material_mode", MATERIAL_MODE_OPTIONS)}
              </div>
              <div>
                <label className={labelClass}>Site Priority</label>
                {optionSelect("priority_list", INC_PRIORITY_OPTIONS)}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            onClick={() => {
              setDraft({})
              setLabels({})
              setStateOpt(null)
              setDistrictOpt(null)
              onReset()
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" /> Reset Filters
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={() => {
                const dateError = firstDateRangeError([
                  [draft.created_from, draft.created_to, "Site Created"],
                  [draft.allocation_from, draft.allocation_to, "Client Allocation"],
                ])
                if (dateError) {
                  toast.error(dateError)
                  return
                }
                onApply(draft, labels)
                onClose()
              }}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 flex items-center gap-2"
            >
              <FiSearch className="w-4 h-4" /> Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Cancel / Hold / Resume modal ────────────────────────────────────────────
interface StatusModalState {
  isOpen: boolean
  action: "cancel" | "hold" | "resume" | null
  site: any
}

interface StatusModalProps {
  state: StatusModalState
  onClose: () => void
  onSuccess: () => void
}

const IncStatusModal: React.FC<StatusModalProps> = ({ state, onClose, onSuccess }) => {
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (state.isOpen) {
      setReason("")
      setError("")
    }
  }, [state.isOpen])

  if (!state.isOpen || !state.action) return null

  const action = state.action
  const meta = {
    cancel: { title: "Cancel Site", btn: "bg-red-600 hover:bg-red-700", note: "bg-red-50 border-red-200 text-red-700", label: "This will cancel the site. This action records a workflow entry." },
    hold: { title: "Hold Site", btn: "bg-yellow-600 hover:bg-yellow-700", note: "bg-yellow-50 border-yellow-200 text-yellow-700", label: "The site will be put on hold and can be resumed later." },
    resume: { title: "Resume Site", btn: "bg-green-600 hover:bg-green-700", note: "bg-green-50 border-green-200 text-green-700", label: "The site will resume from where it was held." },
  }[action]

  const submit = async () => {
    if (action !== "resume" && !reason.trim()) {
      setError("Please provide a reason.")
      return
    }
    setSubmitting(true)
    setError("")
    try {
      await api.put(`/inc/sites/${state.site.id}/${action}`, { remarks: reason.trim() || undefined })
      toast.success(`Site ${action === "cancel" ? "cancelled" : action === "hold" ? "put on hold" : "resumed"}`)
      onSuccess()
      onClose()
    } catch (e: any) {
      setError(e?.response?.data?.message || "Action failed")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {meta.title}: <span className="text-gray-600">{state.site?.site_code}</span>
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div className={`px-3 py-2 rounded border text-xs ${meta.note}`}>{meta.label}</div>
          {action !== "resume" && (
            <textarea
              placeholder={`Reason for ${action}...`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
            />
          )}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded">{error}</div>}
        </div>
        <div className="flex gap-3 p-4 border-t border-gray-200">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={submit} disabled={submitting} className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded disabled:opacity-60 ${meta.btn}`}>
            {submitting ? "Submitting..." : meta.title}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main list ───────────────────────────────────────────────────────────────
interface IncSitesListProps {
  endpoint: string
  title: string
}

const IncSitesList: React.FC<IncSitesListProps> = ({ endpoint, title }) => {
  const { hasPermission } = useAuth()

  const [sites, setSites] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState<PaginationType | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)

  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")

  const [filters, setFilters] = useState<IncFilters>({})
  const [filterLabels, setFilterLabels] = useState<Record<string, string>>({})
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)

  const [sortBy, setSortBy] = useState("created_at")
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC")

  const [statusModal, setStatusModal] = useState<StatusModalState>({ isOpen: false, action: null, site: null })

  const [showExport, setShowExport] = useState(false)
  const [exportData, setExportData] = useState<any[]>([])
  const [exporting, setExporting] = useState(false)

  const canCreate = hasPermission("IncSite", "create")
  const canUpload = hasPermission("IncSite", "upload")
  const canExport = hasPermission("IncSite", "export")
  const canEdit = hasPermission("IncSite", "edit")
  const canCancel = hasPermission("IncSite", "cancel")
  const canHold = hasPermission("IncSite", "hold")

  const debouncedSearchUpdate = useDebouncedCallback((val: string) => {
    setDebouncedSearch(val)
    setPage(1)
  }, 400)

  const activeFiltersCount = useMemo(
    () => Object.values(filters).filter((v) => v !== undefined && v !== "").length,
    [filters],
  )

  const fetchSites = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, any> = { page, limit, sort_by: sortBy, sort_order: sortOrder }
      if (debouncedSearch) params.search = debouncedSearch
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== "") params[k] = v
      })

      const res = await api.get(endpoint, { params })
      const body = res.data?.data ?? {}
      const rows = Array.isArray(body) ? body : body.data ?? []
      setSites(Array.isArray(rows) ? rows : [])
      const pg = Array.isArray(body) ? null : body.pagination
      setPagination(
        pg
          ? {
              offset: (Number(pg.current_page || 1) - 1) * Number(pg.per_page || limit),
              limit: Number(pg.per_page ?? limit),
              page: Number(pg.current_page ?? 1),
              total_pages: Number(pg.total_pages ?? 1),
              total_count: Number(pg.total ?? rows.length),
              has_next_page: !!pg.has_next_page,
              has_prev_page: !!pg.has_prev_page,
              next_page: pg.next_page ?? null,
              prev_page: pg.prev_page ?? null,
            }
          : null,
      )
    } catch {
      toast.error("Failed to fetch sites")
    } finally {
      setLoading(false)
    }
  }, [endpoint, page, limit, sortBy, sortOrder, debouncedSearch, filters])

  useEffect(() => {
    fetchSites()
  }, [fetchSites])

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC")
    } else {
      setSortBy(field)
      setSortOrder("DESC")
    }
  }

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return <FaSort className="w-3 h-3 text-gray-400" />
    return sortOrder === "ASC" ? (
      <FaSortUp className="w-3 h-3 text-blue-800" />
    ) : (
      <FaSortDown className="w-3 h-3 text-blue-800" />
    )
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      const baseParams: Record<string, any> = { limit: 100, sort_by: sortBy, sort_order: sortOrder }
      if (debouncedSearch) baseParams.search = debouncedSearch
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== "") baseParams[k] = v
      })

      let all: any[] = []
      let pageNum = 1
      let totalPages = 1
      do {
        const res = await api.get(endpoint, { params: { ...baseParams, page: pageNum } })
        const body = res.data?.data ?? {}
        const rows = Array.isArray(body) ? body : body.data ?? []
        all = all.concat(rows)
        const pg = Array.isArray(body) ? null : body.pagination
        totalPages = pg ? Number(pg.total_pages || 1) : 1
        pageNum++
      } while (pageNum <= totalPages && pageNum <= 100)

      const mapped = all.map((s: any) => ({
        "Site Code": s.site_code,
        "Reference Number": s.reference_number || "",
        Project: s.project?.project_name || "",
        Activity: s.activity?.activity_name || "",
        Client: s.client?.client_full_name || "",
        "Site Name": s.site_name || "",
        "Site Address": s.site_address || "",
        State: s.state_circle?.circle_name || "",
        District: s.district?.name || "",
        Pincode: s.pincode || "",
        "Work Type": s.work_type || "",
        Priority: s.priority || "",
        "Contact Person": s.contact_person || "",
        "Contact Number": s.contact_number || "",
        "PO Number": s.po_number || "",
        "Team Type": s.team_type || "",
        Supervisor: teamMemberName((s.team_members || []).find((m: any) => m.is_supervisor)?.user),
        "Team Members": (s.team_members || [])
          .map((m: any) => teamMemberName(m.user))
          .filter(Boolean)
          .join(", "),
        "Workflow Status": s.workflow_status || "",
        Material: s.material_mode ? materialModeLabel(s.material_mode) : "",
        Status: s.status || "",
        "Created At": s.created_at ? new Date(s.created_at).toLocaleString() : "",
      }))
      setExportData(mapped)
      setShowExport(true)
    } catch {
      toast.error("Failed to prepare export")
    } finally {
      setExporting(false)
    }
  }

  const removeFilter = (key: keyof IncFilters) => {
    setFilters((prev) => {
      const next = { ...prev }
      delete next[key]
      if (key === "state_circle_id") delete next.district_id
      return next
    })
    setFilterLabels((prev) => {
      const next = { ...prev }
      delete next[key]
      if (key === "state_circle_id") delete next.district_id
      return next
    })
    setPage(1)
  }

  const clearAllFilters = () => {
    setFilters({})
    setFilterLabels({})
    setPage(1)
  }

  const chipText = (key: string, value: string) => {
    const label = FILTER_LABELS[key] || key.replace(/_/g, " ")
    const valueLabel = filterLabels[key] || value
    return `${label}: ${valueLabel}`
  }

  const colSpan = 11

  return (
    <div className="">
      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
              <p className="text-xs text-gray-600 mt-1">Manage INC sites and monitor workflow progress</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              {canExport && (
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
                >
                  <FaFileExcel className="w-4 h-4" />
                  {exporting ? "Preparing..." : "Export Excel"}
                </button>
              )}
              {canUpload && (
                <Link
                  to="/inc/upload"
                  className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
                >
                  <FaUpload className="w-4 h-4" /> Bulk Import
                </Link>
              )}
              {canCreate && (
                <Link
                  to="/inc/create"
                  className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
                >
                  <FaPlus className="w-4 h-4" /> Add New Site
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 max-w-md">
              <label className="block text-xs font-medium text-gray-700 mb-1">Search Sites</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by site code, name or project"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value)
                    debouncedSearchUpdate(e.target.value)
                  }}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
            <div className="w-full sm:w-auto">
              <label className="block text-xs font-medium text-gray-700 mb-1">&nbsp;</label>
              <button
                onClick={() => setIsFilterModalOpen(true)}
                className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center gap-2 transition-colors text-sm relative"
              >
                <FiFilter className="w-4 h-4" />
                Filters
                {activeFiltersCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-blue-800 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Active filter chips */}
          {activeFiltersCount > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-700">Active Filters:</span>
                {Object.entries(filters).map(([key, value]) =>
                  value ? (
                    <span key={key} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {chipText(key, value as string)}
                      <button onClick={() => removeFilter(key as keyof IncFilters)} className="ml-1 text-blue-600 hover:text-blue-800">
                        <FiX className="w-3 h-3" />
                      </button>
                    </span>
                  ) : null,
                )}
                <button onClick={clearAllFilters} className="text-sm text-gray-500 hover:text-gray-700 underline">
                  Clear all
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[140px]">Actions</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[120px]">
                  <button type="button" onClick={() => handleSort("site_code")} className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold">
                    Site Code {getSortIcon("site_code")}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[160px]">Site Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[160px]">Project</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[130px]">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px]">Workflow Stage</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[140px]">Material</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">Team</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[220px]">Team Members</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[140px]">
                  <button type="button" onClick={() => handleSort("created_at")} className="flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase text-xs font-semibold">
                    Created {getSortIcon("created_at")}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 sm:px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                      <span className="text-sm text-gray-600">Loading sites...</span>
                    </div>
                  </td>
                </tr>
              ) : sites.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="px-4 sm:px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                        <FaMapMarkerAlt className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500">No sites found</p>
                      <p className="text-xs text-gray-400 mt-1">Try adjusting your search or create a new site</p>
                    </div>
                  </td>
                </tr>
              ) : (
                sites.map((site) => (
                  <tr key={site.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 flex-wrap">
                        {hasPermission("IncSite", "view") && (
                          <Link to={`/inc/sites/${site.id}`} className="p-2 rounded hover:bg-blue-50 text-blue-600 hover:text-blue-800 transition-colors" title="View">
                            <LuEye className="w-4 h-4" />
                          </Link>
                        )}
                        {canEdit && (
                          <Link to={`/inc/${site.id}/update`} className="p-2 rounded hover:bg-blue-50 text-blue-600 hover:text-blue-800 transition-colors" title="Edit">
                            <FaEdit className="w-4 h-4" />
                          </Link>
                        )}
                        {canHold && site.status === "hold" && (
                          <button onClick={() => setStatusModal({ isOpen: true, action: "resume", site })} className="p-2 rounded hover:bg-green-50 text-green-600 hover:text-green-800 transition-colors" title="Resume">
                            <FaPlay className="w-4 h-4" />
                          </button>
                        )}
                        {canHold && site.status !== "hold" && site.status !== "cancelled" && (
                          <button onClick={() => setStatusModal({ isOpen: true, action: "hold", site })} className="p-2 rounded hover:bg-amber-50 text-amber-600 hover:text-amber-800 transition-colors" title="Hold">
                            <FaPause className="w-4 h-4" />
                          </button>
                        )}
                        {canCancel && site.status !== "cancelled" && (
                          <button onClick={() => setStatusModal({ isOpen: true, action: "cancel", site })} className="p-2 rounded hover:bg-red-50 text-red-600 hover:text-red-800 transition-colors" title="Cancel">
                            <FaBan className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{site.site_code}</td>
                    <td className="px-4 py-4">
                      <span className="truncate max-w-[160px] block text-sm text-gray-700" title={site.site_name || ""}>
                        {site.site_name || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="truncate max-w-[160px] block text-sm text-gray-700" title={site.project?.project_name || ""}>
                        {site.project?.project_name || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(site.status)}</td>
                    <td className="px-4 py-4 whitespace-nowrap">{getWorkflowBadge(site.workflow_status)}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {site.material_mode ? <MaterialModeBadge mode={site.material_mode} /> : <span className="text-xs text-gray-400">Not decided</span>}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">{getPriorityBadge(site.priority)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 capitalize">{(site.team_type || "-").replace(/_/g, " ")}</td>
                    <td className="px-4 py-4">
                      {site.team_members?.length ? (
                        <div className="flex flex-wrap gap-1 max-w-[280px]">
                          {site.team_members.map((m: any) => (
                            <span
                              key={m.id}
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                m.is_supervisor ? "bg-blue-100 text-blue-900 border border-blue-200" : "bg-gray-100 text-gray-800"
                              }`}
                              title={[m.user?.designation?.name, m.is_supervisor ? "Supervisor" : ""].filter(Boolean).join(" · ")}
                            >
                              {teamMemberName(m.user) || `User #${m.user_id}`}
                              {m.is_supervisor && <span className="ml-1 text-[10px] uppercase text-blue-700">Sup</span>}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Not assigned</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {site.created_at ? new Date(site.created_at).toLocaleDateString() : "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && (
          <Pagination pagination={pagination} onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1) }} />
        )}
      </div>

      {/* Filter modal */}
      <IncFilterModal
        isOpen={isFilterModalOpen}
        teamScope={endpoint === "/inc/sites" ? "mine" : "all"}
        initial={filters}
        initialLabels={filterLabels}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={(f, labels) => {
          setFilters(f)
          setFilterLabels(labels)
          setPage(1)
        }}
        onReset={() => {
          setFilters({})
          setFilterLabels({})
          setPage(1)
        }}
      />

      {/* Status modal */}
      <IncStatusModal state={statusModal} onClose={() => setStatusModal({ isOpen: false, action: null, site: null })} onSuccess={fetchSites} />

      {/* Export modal */}
      {showExport && (
        <ExcelDownload
          isOpen={showExport}
          data={exportData}
          filename="inc_sites"
          title="Export INC Sites"
          description="Download INC site details"
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  )
}

export default IncSitesList
